import { forwardRef, useCallback } from 'react';
import { IconTickMedium } from '@izettle/otto';
import { SelectRadioButton, StyledInputIcon, SelectOptionContainer, FocusRemover, StyledImage, SelectCheckbox, SelectOptionContent, SelectOptionText, SelectOptionDescription } from './styles.js';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { KeyCodes } from '../../../enumerations/key-codes.js';
import { SemanticState } from '../../../enumerations/semantic-state.js';

const SelectOption = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    id,
    key,
    text,
    image,
    description,
    isExpanded = false,
    isMultiple = false,
    isMobile = false,
    isSelected = false,
    onSelect,
    onOptionItemFocused,
    onNext,
    onPrevious,
    onTab,
    onEscape,
    disabled
  } = _ref;
  const handleKeyDown = useCallback(event => {
    if (!isExpanded) {
      return;
    }
    switch (event.which) {
      case KeyCodes.Up:
        {
          event.preventDefault();
          onPrevious?.();
          break;
        }
      case KeyCodes.Down:
        {
          event.preventDefault();
          onNext?.();
          break;
        }
      case KeyCodes.Enter:
        {
          if (disabled) break;
          event.preventDefault();
          onSelect?.();
          break;
        }
      case KeyCodes.Tab:
        onTab?.();
        break;
      case KeyCodes.Escape:
        onEscape?.();
        break;
    }
  }, [isExpanded, onPrevious, onNext, onEscape]);
  const renderRadioButton = useCallback(() => {
    if (isMobile) {
      return /*#__PURE__*/jsx(SelectRadioButton, {
        id: `radio-button-${id}`,
        checked: isSelected,
        disabled: disabled
      });
    }
  }, [isSelected, isMobile, disabled]);
  const renderTick = useCallback(() => {
    if (isSelected && !isMobile) {
      return /*#__PURE__*/jsx(StyledInputIcon, {
        semanticState: SemanticState.Success,
        block: true,
        svgIcon: IconTickMedium
      });
    }
  }, [isSelected, isMobile]);
  return /*#__PURE__*/jsx(SelectOptionContainer, {
    id: id,
    role: "option",
    "aria-hidden": !isExpanded,
    "aria-selected": isSelected,
    "aria-checked": isSelected,
    tabIndex: key === 0 ? 0 : -1,
    onClick: () => !disabled && onSelect?.(),
    isSelected: isSelected,
    isMultiple: isMultiple,
    onKeyDown: handleKeyDown,
    disabled: disabled,
    "aria-label": `${text}, ${description}`,
    children: /*#__PURE__*/jsx(FocusRemover, {
      tabIndex: -1,
      ref: ref,
      onFocus: onOptionItemFocused,
      onKeyDown: handleKeyDown,
      children: isMultiple ? /*#__PURE__*/jsxs(Fragment, {
        children: [image && /*#__PURE__*/jsx(StyledImage, {
          src: image,
          "aria-hidden": true,
          role: "presentation"
        }), /*#__PURE__*/jsx(SelectCheckbox, {
          id: `checkbox-${id}`,
          checked: isSelected,
          disabled: disabled
        }), /*#__PURE__*/jsxs(SelectOptionContent, {
          children: [/*#__PURE__*/jsx(SelectOptionText, {
            "aria-hidden": true,
            disabled: disabled,
            children: text
          }), description && /*#__PURE__*/jsx(SelectOptionDescription, {
            "aria-hidden": true,
            children: description
          })]
        })]
      }) : /*#__PURE__*/jsxs(Fragment, {
        children: [renderRadioButton(), image && /*#__PURE__*/jsx(StyledImage, {
          src: image,
          "aria-hidden": "true",
          role: "presentation"
        }), /*#__PURE__*/jsxs(SelectOptionContent, {
          children: [/*#__PURE__*/jsx(SelectOptionText, {
            disabled: disabled,
            "aria-hidden": true,
            children: text
          }), description && /*#__PURE__*/jsx(SelectOptionDescription, {
            "aria-hidden": true,
            children: description
          })]
        }), renderTick()]
      })
    })
  });
});

export { SelectOption };
//# sourceMappingURL=select-option.js.map
